import { useEffect, useMemo, useState } from "react";
import {
  Trophy, TrendingUp, User as UserIcon, Users, UserCog, Crown,
  ScrollText, Filter, CheckCircle2, Lock,
} from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Icons for each level to match the reference
const LEVEL_ICONS = {
  1: UserIcon, 2: Users, 3: TrendingUp, 4: Crown,
  5: Users, 6: Users, 7: UserCog, 8: Crown,
};

const numberBadge = (n) =>
  `inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-black
   border-2 border-amber-500 bg-white text-amber-700 shadow`;

export default function RankIncome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [fId, setFId] = useState("");
  const [fName, setFName] = useState("");
  const [fLevel, setFLevel] = useState("All");
  const [fRank, setFRank] = useState("All");
  const [fPaying, setFPaying] = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rank-income");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/rank-income/history");
        setHistory(data.items || []);
      } catch (e) { console.error("Failed to load rank income history:", e); }
      finally { setHistoryLoading(false); }
    })();
  }, []);

  const uniqueRanks = useMemo(() => {
    const s = new Set(history.map((h) => h.rank));
    return ["All", ...Array.from(s)];
  }, [history]);

  const filtered = useMemo(() => {
    const idQ = fId.trim().toLowerCase();
    const nameQ = fName.trim().toLowerCase();
    return history.filter((h) => {
      if (idQ && !h.user_code.toLowerCase().includes(idQ)) return false;
      if (nameQ && !(h.name || "").toLowerCase().includes(nameQ)) return false;
      if (fLevel !== "All" && String(h.level) !== fLevel) return false;
      if (fRank !== "All" && h.rank !== fRank) return false;
      if (fPaying === "Paying" && !h.paying) return false;
      if (fPaying === "Non-paying" && h.paying) return false;
      return true;
    });
  }, [history, fId, fName, fLevel, fRank, fPaying]);

  const formatDT = (iso) => {
    if (!iso) return { date: "—", time: "" };
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const t = d.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
      return { date: `${dd}/${mm}/${yyyy}`, time: t };
    } catch { return { date: "—", time: "" }; }
  };

  const rankPill = (rank) => {
    const map = {
      Executive: "bg-blue-100 text-blue-700",
      Promoter: "bg-emerald-100 text-emerald-700",
      Motivator: "bg-purple-100 text-purple-700",
      "Sr. Motivator": "bg-fuchsia-100 text-fuchsia-700",
      "Co-ordinator": "bg-amber-100 text-amber-800",
      "Sr. Co-ordinator": "bg-orange-100 text-orange-800",
      Manager: "bg-red-100 text-red-700",
      Director: "bg-rose-100 text-rose-800",
    };
    return `inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${
      map[rank] || "bg-slate-100 text-slate-700"
    }`;
  };

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-white">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <Trophy size={28} className="text-amber-300" />
            Rank Income
          </h1>
          <p className="text-white/70 mt-1 text-sm">
            Every rank-qualified member <b>after the first 2</b> at each level earns you a fixed payout
          </p>
        </div>

        {loading || !data ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Total */}
            <section
              data-testid="total-rank-income-card"
              className="rounded-2xl p-5 sm:p-6 shadow-xl relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #f59e0b 0%, #d97706 40%, #b45309 100%)",
              }}
            >
              <div className="absolute -right-6 -top-6 h-32 w-32 bg-white/15 rounded-full blur-2xl" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-widest text-white/85 font-bold">
                  Total Rank Income Earned
                </div>
                <div className="mt-2 text-4xl sm:text-5xl font-black text-white"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {INR(data.total_income)}
                </div>
                <div className="mt-2 text-xs text-white/80">
                  Payout starts from the 3rd qualified member at each level (up to unlimited)
                </div>
              </div>
            </section>

            {/* Slab table styled like reference */}
            <section
              data-testid="rank-income-slabs"
              className="rounded-2xl overflow-hidden shadow-2xl"
              style={{
                background: "linear-gradient(180deg, #f59e0b 0%, #f8fafc 8%)",
              }}
            >
              <div className="grid grid-cols-[1.1fr_1.4fr_1fr] items-center bg-slate-100">
                <div className="px-4 py-3 bg-red-700 text-white text-center font-extrabold tracking-widest"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  RANK
                </div>
                <div className="px-4 py-3 bg-blue-900 text-white text-center font-extrabold tracking-widest"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  DESIGNATION
                </div>
                <div className="px-4 py-3 bg-emerald-600 text-white text-center font-extrabold tracking-widest"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  INCOME
                </div>
              </div>
              <ul className="divide-y divide-slate-200">
                {data.slabs.map((s) => {
                  const Icon = LEVEL_ICONS[s.depth] || UserIcon;
                  const paid = s.income > 0;
                  return (
                    <li
                      key={s.depth}
                      data-testid={`rank-slab-${s.depth}`}
                      className={`grid grid-cols-[1.1fr_1.4fr_1fr] items-center bg-white`}
                    >
                      <div className="px-4 py-3 flex items-center gap-2 border-r border-slate-200">
                        <span className={numberBadge(s.depth)}>{s.depth}</span>
                        <span className="font-bold text-slate-800">{s.depth === 1 ? "1st" : s.depth === 2 ? "2nd" : s.depth === 3 ? "3rd" : `${s.depth}th`} Level</span>
                      </div>
                      <div className="px-4 py-3 flex items-center gap-3 border-r border-slate-200">
                        <Icon size={20} className="text-slate-700 shrink-0" />
                        <span className="font-bold text-slate-800">({s.rank})</span>
                        {paid && <CheckCircle2 size={14} className="text-emerald-600" />}
                      </div>
                      <div className="px-4 py-3 flex flex-col items-center">
                        <span className="text-red-700 font-black text-lg tabular-nums"
                              style={{ fontFamily: "Outfit, sans-serif" }}>
                          {INR(s.per_member)}
                        </span>
                        <span className="text-[10px] text-slate-500 mt-0.5">
                          {s.chargeable} × {INR(s.per_member)} = <b>{INR(s.income)}</b>
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {s.count} qualified · first {s.free_first} free
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <div className="rounded-2xl bg-white/10 border border-white/15 p-4 text-xs text-white/80 leading-relaxed">
              <b className="text-amber-300">How it works:</b> At each level, your first {" "}
              <b>2 rank-qualified members are free</b>. From the 3rd onwards (unlimited), every
              matching member pays you the level's fixed income. Payouts scale up with the rank.
            </div>

            {/* Rank Income History */}
            <section
              data-testid="rank-income-history-section"
              className="bg-white rounded-2xl shadow-lg overflow-hidden text-slate-900"
            >
              <div className="px-5 pt-5 pb-4 border-b border-slate-100">
                <h2 className="text-xl font-extrabold flex items-baseline gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <ScrollText size={18} className="text-red-700 self-center" />
                  Rank Income <span className="text-slate-500 font-semibold">History</span>
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Every rank-qualified member in your team (paying and non-paying)
                </p>
              </div>

              <div className="p-5 border-b border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest mb-3">
                  <Filter size={13} /> Filters
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User ID</label>
                    <input
                      data-testid="ri-filter-id"
                      value={fId}
                      onChange={(e) => setFId(e.target.value)}
                      placeholder="Search User ID..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">User Name</label>
                    <input
                      data-testid="ri-filter-name"
                      value={fName}
                      onChange={(e) => setFName(e.target.value)}
                      placeholder="Search User Name..."
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Filter by Level (1-8)</label>
                    <select
                      data-testid="ri-filter-level"
                      value={fLevel}
                      onChange={(e) => setFLevel(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      <option value="All">All Levels</option>
                      {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={String(n)}>Level {n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Filter by Rank</label>
                    <select
                      data-testid="ri-filter-rank"
                      value={fRank}
                      onChange={(e) => setFRank(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      {uniqueRanks.map((r) => (
                        <option key={r} value={r}>{r === "All" ? "All Ranks" : r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Contribution</label>
                    <select
                      data-testid="ri-filter-paying"
                      value={fPaying}
                      onChange={(e) => setFPaying(e.target.value)}
                      className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      <option value="All">All</option>
                      <option value="Paying">Paying only</option>
                      <option value="Non-paying">Non-paying (first 2)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
                    <tr>
                      <th className="text-left px-4 py-3">SR. NO.</th>
                      <th className="text-left px-4 py-3">User ID</th>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Level</th>
                      <th className="text-left px-4 py-3">Rank</th>
                      <th className="text-left px-4 py-3">Position</th>
                      <th className="text-left px-4 py-3">Amount (₹)</th>
                      <th className="text-left px-4 py-3">Achievement Date</th>
                    </tr>
                  </thead>
                  <tbody data-testid="ri-history-table" className="divide-y divide-slate-100">
                    {historyLoading ? (
                      <tr><td colSpan={8} className="text-center py-10 text-slate-500">Loading…</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-10 text-slate-500">
                        No matching entries.
                      </td></tr>
                    ) : filtered.map((h, i) => {
                      const a = formatDT(h.achievement_date);
                      return (
                        <tr key={h.user_code + h.level} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-800">{i + 1}</td>
                          <td className="px-4 py-3 font-mono text-blue-700 font-semibold">{h.user_code}</td>
                          <td className="px-4 py-3 font-semibold">{h.name}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              Level {h.level}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={rankPill(h.rank)}>{h.rank}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            #{h.position_at_depth}{" "}
                            {h.paying ? (
                              <span className="text-emerald-600 text-xs font-semibold">· paying</span>
                            ) : (
                              <span className="text-slate-400 text-xs">· free</span>
                            )}
                          </td>
                          <td className={`px-4 py-3 font-extrabold tabular-nums ${
                            h.paying ? "text-emerald-600" : "text-slate-400"
                          }`}>
                            {h.paying ? `₹${Number(h.amount).toLocaleString("en-IN")}` : "₹0"}
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            <span className="font-semibold">{a.date}</span>{" "}
                            <span className="text-slate-500 text-xs">{a.time}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600">
                Showing <b>{filtered.length}</b> of <b>{history.length}</b> entries
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
